/**
 * FormWorkflowActionProvider
 *
 * Single owner of the form's workflow actions — run workflow, change state,
 * close & create, export, email — plus the modals they open, so the toolbar
 * dropdown, the right-click menu, and the command palette share one code path.
 * Mounted by `FormPageContent` and by the layout editor's `PreviewFormMount`.
 *
 * Subscribes to six leaves only: the ids, the version state, and the form-type
 * config that decide which entries exist and whether they are enabled. It must
 * never read `useFormStore()` — that hook spreads `formVersion.data`, so a
 * single keystroke would invalidate this provider and, through the context
 * value, every consumer. Document content the actions need (field values,
 * layout) never reaches this render: `checkRequiredFields` reads it off the live
 * proxy at click time, and the workflow sidebar's source values are subscribed
 * one level down, inside `OpenFormWorkflowParameterSidebar`.
 */
import { useContext, useMemo, useState, type ReactNode } from 'react';
import { useSnapshot } from 'valtio';
import { formStore } from '../../store';
import { MessageContext } from '../MessageContext';
import { useFormCloseAndCreate } from '../../use/form/useFormCloseAndCreate';
import { useFormWorkflowRun } from '../../use/form/useFormWorkflowRun';
import { useFormStateChange } from '../../use/form/useFormStateChange';
import { useFormExport } from '../../use/form/useFormExport';
import { useFormEmail } from '../../use/form/useFormEmail';
import {
  FormWorkflowActionContext,
  type FormWorkflowActionContextValue,
} from './FormWorkflowActionContext';

type FormWorkflowActionProviderProps = {
  formId: string;
  children: ReactNode;
};

export function FormWorkflowActionProvider({ formId, children }: FormWorkflowActionProviderProps) {
  const { messageApi } = useContext(MessageContext);
  const snap = useSnapshot(formStore.state);
  const versionId = snap.versionId;
  const versionState = snap.formVersion.data?.state;
  const formType = snap.form.data?.formType;
  const formNumber = snap.form.data?.formNumber;
  const possibleStates = snap.formType.data?.possibleStates;
  const allowedNextForms = snap.formType.data?.allowedNextForms;

  const [isCreating, setIsCreating] = useState(false);

  const {
    closeAndCreateDrawer,
    allowedNextStates,
    allowedNextForms: allowedNextFormTypes,
    onOpenCloseAndCreate,
    isCloseAndCreateEntryDisabled,
    onCreateNextForm,
    isCreateNextFormDisabled,
  } = useFormCloseAndCreate({
    formId,
    versionId,
    versionState,
    possibleStates,
    allowedNextForms,
    messageApi,
    isCreating,
    setIsCreating,
  });

  const {
    workflowParameterSidebar,
    applicableWorkflows,
    handleWorkflowClick,
  } = useFormWorkflowRun({
    formId,
    formType,
    formNumber,
    versionId,
    messageApi,
    setIsCreating,
  });

  const { stateChangeModal, handleSetState } = useFormStateChange({
    formId,
    versionId,
    messageApi,
    isCreating,
    setIsCreating,
  });

  const { exportModal, handleExport } = useFormExport();

  const { emailModal, handleEmailClick } = useFormEmail({ formId, formNumber });

  const value: FormWorkflowActionContextValue = useMemo(() => ({
    formId,
    isCreating,
    setIsCreating,
    allowedNextStates,
    allowedNextForms: allowedNextFormTypes,
    onOpenCloseAndCreate,
    isCloseAndCreateEntryDisabled,
    onCreateNextForm,
    isCreateNextFormDisabled,
    applicableWorkflows,
    handleWorkflowClick,
    handleSetState,
    handleExport,
    handleEmailClick,
  }), [
    formId,
    isCreating,
    allowedNextStates,
    allowedNextFormTypes,
    onOpenCloseAndCreate,
    isCloseAndCreateEntryDisabled,
    onCreateNextForm,
    isCreateNextFormDisabled,
    applicableWorkflows,
    handleWorkflowClick,
    handleSetState,
    handleExport,
    handleEmailClick,
  ]);

  return (
    <FormWorkflowActionContext.Provider value={value}>
      {children}
      {closeAndCreateDrawer}
      {workflowParameterSidebar}
      {stateChangeModal}
      {exportModal}
      {emailModal}
    </FormWorkflowActionContext.Provider>
  );
}
